/**
 * Composes Material UI `theme.components` overrides used by `createTheme` in `themes/createTheme.ts`.
 * Each sibling `mui*.ts` file maps to one or more `components.Mui*` keys — search this folder for `MuiPaper`, etc.
 *
 *
 */
import type { Components, Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import { muiAppBar } from './muiAppBar';
import { muiAvatar } from './muiAvatar';
import { muiBackdrop } from './muiBackdrop';
import { muiButton } from './muiButton';
import { muiButtonBase } from './muiButtonBase';
import { muiCard } from './muiCard';
import { muiChip } from './muiChip';
import { muiCssBaseline } from './muiCssBaseline';
import { muiDialog } from './muiDialog';
import { muiFeedback } from './muiFeedback';
import { muiFormHelperText } from './muiFormHelperText';
import { muiFormLabel } from './muiFormLabel';
import { muiIconButton } from './muiIconButton';
import { muiInputs } from './muiInputs';
import { muiList } from './muiList';
import { muiMenu } from './muiMenu';
import { muiModal } from './muiModal';
import { muiPaper } from './muiPaper';
import { muiPickers } from './muiPickers';
import { muiSelection } from './muiSelection';
import { muiStepIcon } from './muiStepIcon';
import { muiTabs } from './muiTabs';
import { muiToggle } from './muiToggle';
import { muiTypography } from './muiTypography';

/**
 *
 * @param theme - this is the global theme w/o MUI components
 * @returns MUI component structure, pass to create stream in components object
 *
 * Note: You can use MUI theme in styleOverrides by providing a function for the key value which takes
 *
 */
const CreateMuiComponents = (theme: Theme): Components<Theme> => {
  return {
    ...muiButtonBase(theme),
    ...muiAvatar(theme),
    ...muiAppBar(theme),
    ...muiCssBaseline(theme),
    ...muiFormHelperText(theme),
    ...muiFormLabel(theme),
    ...muiList(theme),
    ...muiModal(theme),
    ...muiMenu(theme),
    ...muiPaper(theme),
    ...muiStepIcon(theme),
    ...muiCard(theme),
    ...muiFeedback(theme),
    ...muiInputs(theme),
    ...muiBackdrop(theme),
    ...muiToggle(theme),
    ...muiIconButton(theme),
    ...muiButton(theme),
    ...muiChip(theme),
    ...muiDialog(theme),
    ...muiPickers(theme),
    ...muiTabs(theme),
    ...muiSelection(theme),
    ...muiTypography(theme),
  } as Components<Theme>;
};

export default CreateMuiComponents;
